import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { requiredArrayValidator } from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import ShadowCard from "../../components/common/ShadowCard";
import IconComponent from "../../components/common/IconComponent";
import {
  deleteCareerImage,
  getCareerDetails,
  updateCareer,
} from "./services/career.services";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import FormFileUpload from "../../components/formFields/FormFileUpload";
import { MdDelete } from "react-icons/md";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { clearCareerImageID, setCareerImageID } from "./careerSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";

const Career: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const [formKey, setFormKey] = useState(1);

  const loading = useAppSelector((state) => state.career.loading);
  const CareerDetails = useAppSelector((state) => state.career.CareerDetails);

  useEffect(() => {
    dispatch(getCareerDetails());
  }, []);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [CareerDetails]);

  const handleDeleteImage = (ID: number) => {
    dispatch(openDialog("deleteCareerImage"));
    dispatch(setCareerImageID(ID));
  };

  const handleSubmit = async (values: any) => {
    const ImageData = new FormData();
    const BannerImage =
      values?.BannerImage && typeof values?.BannerImage !== "string"
        ? values.BannerImage[0].getRawFile()
        : "";

    try {
      ImageData.append("BannerImage", BannerImage);
      ImageData.append("Description", values?.Description);
      if (values?.attachments && Array.isArray(values?.attachments)) {
        values?.attachments.forEach((image: any) => {
          if (image?.attachment && typeof image?.attachment !== "string")
            return;
          const file = image instanceof File ? image : image?.getRawFile();
          if (file) {
            ImageData.append("attachment", file);
          }
        });
      }
      const response = await dispatch(updateCareer(ImageData));
      if (response?.meta?.requestStatus === "fulfilled") {
        dispatch(getCareerDetails());
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <DeleteCareerImageDialog />
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={CareerDetails}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <Typography.h4>{"Career Details"}</Typography.h4>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 20 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                <GridLayoutItem colSpan={3}>
                  <Field
                    name="Description"
                    label="Career Description"
                    component={FormRichTextField}
                    contentHeight={250}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    justifyContent="start"
                    name={`BannerImage`}
                    label="Banner Image"
                    type="file"
                    accept="image/*"
                    alternateName="Banner Image"
                    component={FormImageUpload}
                    validator={requiredArrayValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem style={{ marginTop: 10 }}>
                  <Field
                    justifyContent="start"
                    name="attachments"
                    label="Attachment"
                    type="file"
                    accept="image/*"
                    alternateName="attachment"
                    component={FormFileUpload}
                    multiple
                  />
                </GridLayoutItem>
                {CareerDetails?.CarrerImages &&
                  CareerDetails?.CarrerImages?.length > 0 && (
                    <GridLayoutItem colSpan={3} className="mt-3">
                      <div className="card shadow-sm border-0">
                        {CareerDetails?.CarrerImages?.length > 0 && (
                          <div
                            className="card-body p-2 pt-3 pe-3"
                            style={{
                              background: "#f8f9fa",
                              borderRadius: "8px",
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h5
                                className="mb-0"
                                style={{
                                  color: "#2c3e50",
                                  fontWeight: "600",
                                  fontSize: "1.1rem",
                                }}
                              >
                                Uploaded Images
                              </h5>
                              <span className="badge bg-kendo-primary rounded-pill">
                                {CareerDetails?.CarrerImages?.length || 0} files
                              </span>
                            </div>

                            <div className="d-flex flex-wrap gap-3">
                              {CareerDetails?.CarrerImages?.map(
                                (image: any) => (
                                  <div
                                    key={image.ID}
                                    className="position-relative"
                                    style={{
                                      width: "150px",
                                      transition: "all 0.3s ease",
                                    }}
                                  >
                                    <div className="card h-100 border-0 shadow-sm overflow-hidden">
                                      <div className="ratio ratio-1x1 bg-light overflow-hidden">
                                        <div
                                          style={{
                                            position: "absolute",
                                            width: "100%",
                                            height: "auto",
                                            display: "flex",
                                            justifyContent: "end",
                                          }}
                                        >
                                          <Button
                                            themeColor={"error"}
                                            fillMode={"solid"}
                                            style={{
                                              position: "relative",
                                              padding: "5px 10px",
                                              width: 30,
                                              height: 30,

                                              color: "#fff",
                                              zIndex: "1",
                                              borderRadius: "0 0 0 5px",
                                            }}
                                            onClick={() =>
                                              handleDeleteImage(image.ID)
                                            }
                                          >
                                            <IconComponent
                                              icon={MdDelete}
                                              className="absolute-position fs-5"
                                            />
                                          </Button>
                                        </div>
                                        <img
                                          className="img-fluid object-fit-cover"
                                          src={image.Image}
                                          alt="Uploaded document"
                                          style={{
                                            cursor: "pointer",
                                            transition: "transform 0.3s",
                                          }}
                                          onMouseEnter={(e) =>
                                            (e.currentTarget.style.transform =
                                              "scale(1.05)")
                                          }
                                          onMouseLeave={(e) =>
                                            (e.currentTarget.style.transform =
                                              "scale(1)")
                                          }
                                          onClick={() =>
                                            window.open(
                                              `${image.Image}`,
                                              "_blank"
                                            )
                                          }
                                        />
                                      </div>
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </GridLayoutItem>
                  )}
                <GridLayoutItem
                  colSpan={3}
                  style={{
                    textAlign: "end",
                    marginTop: 20,
                    width: "100%",
                  }}
                >
                  <ButtonWithLoading
                    label={"Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    style={{ marginLeft: 10 }}
                  >
                    Cancel
                  </Button>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
    </>
  );
};

const DeleteCareerImageDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const CareerImageID = useAppSelector((state) => state.career.CareerImageID);

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteCareerImage(ID));
      if (response.meta.requestStatus === "fulfilled") {
        dispatch(getCareerDetails());
        dispatch(closeDialog());
        dispatch(clearCareerImageID());
      } else {
        dispatch(getCareerDetails());
        dispatch(closeDialog());
        dispatch(clearCareerImageID());
      }
    }
  };
  return (
    <>
      {dialogName === "deleteCareerImage" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Image?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Image?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(CareerImageID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default Career;
